/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  IS_REGION_AU_OR_NZ,
  IS_REGION_US,
  NumberTypes,
  queryFormEditable,
  queryFormMode,
  State,
} from '@gh/common-shared';
import { isEditFormMode } from '@gh/core-ui';
import { inDefaultCurrency } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { HlpQuoteDetailsCommandService } from '../hlp-quote-details-command.service';
import {
  HlpQuoteDetailsState,
  queryQuoteBottomInfo,
  queryQuoteDerivedInfo,
  queryQuoteIsApproved,
  queryQuoteIsProduced,
  queryQuoteIsProfitSupported,
  queryQuoteIsProfitVisible,
} from '../hlp-quote-details.state';
import { HlpDetailsBusinessService } from '../../../../resources/hlp-details-business.service';

@Component({
  selector:    'gh-hlp-quote-details-bottom-panel',
  template: require('./hlp-quote-details-bottom-panel.component.html'),
  styles:   [require('./hlp-quote-details-bottom-panel.component.scss')],
})
export class HlpQuoteDetailsBottomPanelComponent implements OnInit {
  memo$: Observable<string>;
  isMemoOnTop$: Observable<boolean>;
  rfqPrice1$: Observable<number>;
  rfqPrice2$: Observable<number>;
  profit$: Observable<number>;
  pbSalesPrice$: Observable<number>;
  pbCost$: Observable<number>;

  origSalesTax$: Observable<number>;
  adjSalesTax$: Observable<number>;
  totalSalesTax$: Observable<number>;

  displayPrice$: Observable<number>;

  canApprove$: Observable<boolean>;
  canUnapprove$: Observable<boolean>;
  supportsProfit$: Observable<boolean>;
  isProfitVisible$: Observable<boolean>;
  approvedByName$: Observable<Maybe<string>>;
  approvedDate$: Observable<Maybe<Date>>;

  calculationNumber$: Observable<number>;
  calculationStr$: Observable<string>;

  profitOppositeLabel$: Observable<string>;

  editMode$: Observable<boolean>;
  editable$: Observable<boolean>;

  usRegion = IS_REGION_US;
  auOrNzRegion = IS_REGION_AU_OR_NZ;

  constructor(
    public businessService: HlpDetailsBusinessService,
    private userContext: BuilderUserContext,
    private state: State<HlpQuoteDetailsState>,
    private commands: HlpQuoteDetailsCommandService,
  ) {

  }

  ngOnInit(): void {
    const bottom$ = this.state.select(queryQuoteBottomInfo);
    const derived$ = this.state.select(queryQuoteDerivedInfo);

    this.editMode$ = this.state.select(queryFormMode).pipe(map$(isEditFormMode));
    this.editable$ = this.state.select(queryFormEditable);

    this.memo$ = bottom$.pipe(map$((bottom) => bottom.memo));
    this.isMemoOnTop$ = bottom$.pipe(map$((bottom) => bottom.isMemoOnTop));
    this.rfqPrice1$ = derived$.pipe(map$((derived) => derived.rfqPrice1));
    this.rfqPrice2$ = derived$.pipe(map$((derived) => derived.rfqPrice2));
    this.profit$ = derived$.pipe(map$((derived) => derived.profit));
    this.pbSalesPrice$ = derived$.pipe(map$((derived) => derived.pbSalesPrice));
    this.pbCost$ = derived$.pipe(map$((derived) => derived.pbCost));
    this.origSalesTax$ = derived$.pipe(map$((derived) => derived.origSalesTax));
    this.adjSalesTax$ = derived$.pipe(map$((derived) => derived.adjSalesTax));
    this.totalSalesTax$ = derived$.pipe(map$((derived) => derived.totalSalesTax));
    this.approvedByName$ = derived$.pipe(map$((derived) => derived.approvedByName));
    this.approvedDate$ = derived$.pipe(map$((derived) => derived.approvedDate));
    this.calculationNumber$ = derived$.pipe(map$(({ pbPrice, pbCost, profit }) => pbPrice - pbCost + profit));
    this.calculationStr$ = derived$.pipe(map$(({ pbPrice, pbCost, profit }) => profit >= 0 ?
      // tslint:disable-next-line:max-line-length
      `${ inDefaultCurrency(NumberTypes.Price.format(pbPrice)) } - ${ NumberTypes.Price.format(pbCost) } + ${ NumberTypes.Price.format(
        profit) }` :
      // tslint:disable-next-line:max-line-length
      `${ inDefaultCurrency(NumberTypes.Price.format(pbPrice)) } - ${ NumberTypes.Price.format(pbCost) } - ${ NumberTypes.Price.format(
        Math.abs(profit)) }`));

    const producedAndApproved$ = combineLatest$(
      this.state.select(queryQuoteIsProduced),
      this.state.select(queryQuoteIsApproved),
    );

    this.canApprove$ = producedAndApproved$.pipe(map$(([produced, approved]) => produced && !approved));
    this.canUnapprove$ = producedAndApproved$.pipe(map$(([produced, approved]) => produced && approved));

    this.supportsProfit$ = this.state.select(queryQuoteIsProfitSupported);
    this.isProfitVisible$ = this.state.select(queryQuoteIsProfitVisible);

    this.profitOppositeLabel$ = this.isProfitVisible$.pipe(map$((isVisible) =>
      isVisible ? 'lbl_hide_profit' : 'lbl_show_profit'));
  }

  onToggleProfitVisiblity(): void {
    this.commands.toggleProfitVisiblity(!this.state.project(queryQuoteIsProfitVisible));
  }
  onDisplayPriceChange(event: any): void {
    const value = event.target.value ? event.target.value.split(',').join('').split('.').join('') : '0';
    this.businessService.hlpDetails.commonDetails.displayPrice = value;
    this.businessService.mainForm.get('commonDetails.displayPrice').setValue(value);
  }
  onOverrideChange(event: any): void {
    this.businessService.hlpDetails.isDisplayPriceOverridden = event.checked;
    if (!event.checked) {
      this.businessService.mainForm.get('commonDetails.displayPrice').setValue(this.businessService.hlpDetails.commonDetails.displayPrice);
    }
    const control = this.businessService.mainForm.get('commonDetails.isDisplayPriceOverridden');
    control.setValue(event.checked);
    control.markAsDirty();
  }
}
